import * as React from 'react'

import RLGDebug from 'debug'

const now = require('performance-now')

import { Block } from './components/Block'
import { IMenuItem } from './components/blockTypes'
import { ParamValue } from './components/Params'
import { ContextMenu } from './editors/ContextMenu'

import { Select } from './editors/Select'
import { IGenerator } from './generators/Generator'
import { DebugLevels, ServiceOptions, IPoint } from './types'
import { content } from './LayoutContent'
import {
  ILayoutProps,
  ILayoutState,
  gLayouts,
  ILayoutInstance
} from './LayoutTypes'
import { mainStyle } from './LayoutStyle'

const raf = require('raf')

/**
 * internal use only
 * @ignore
 */
let gLayoutRoot: Layout

export { gLayoutRoot }

/**
 * Layout manages a layout. See [ILayoutProps](interfaces/ilayoutprops.html)
 * for detail properties.
 * Usage:
 * ```ts
 *  <Layout
 *    name={'Layout Name'}
 *    g={ generator }
 *  >
 *    { content }
 *  </Layout>
 * ```
 * @noInheritDoc
 */
export class Layout extends React.Component<ILayoutProps, ILayoutState>
  implements ILayoutInstance {
  get select() {
    return this._select
  }

  private _root: HTMLDivElement | undefined = undefined
  private _g: IGenerator

  private _edit: boolean = false
  private _startRendering: number = now()

  private _count: number = 0
  private _select: Select | undefined = undefined
  private _menuLocation: IPoint = { x: 0, y: 0 }
  private _rafId: number = 0
  private _lastAnimationFrame: number = 0
  private _fpsIntervalFrame: number = 0

  constructor(props: ILayoutProps) {
    super(props)
    this.state = {
      boundingLeftTop: { x: 0, y: 0 },
      height: props.containersize ? props.containersize.height : 0,
      update: 0,
      width: props.containersize ? props.containersize.width : 0,
      contextMenu: undefined,
      contextMenuActive: false,
      devicePixelRatio: window.devicePixelRatio
    }

    if (props.g && props.g.blocks().size !== 0) {
      RLGDebug(
        DebugLevels.warning
      )(`Layout: Did you intend on reusing this generator in ${props.name}? 
      If so you should clear it first by calling g.clear()`)
    }

    this.initProps(props)
  }

  public componentWillMount() {
    if (!gLayoutRoot) {
      gLayoutRoot = this
    }
  }

  public componentDidMount() {
    if (gLayouts.get(this.props.name) !== undefined) {
      RLGDebug(DebugLevels.error)(`
      Did you reuse the name ${
        this.props.name
      }?. Each Layout name must be unique.
      `)
    }

    gLayouts.set(this.props.name, true)

    window.addEventListener('resize', this.onWindowResize)
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize)

    gLayouts.delete(this.props.name)
    if (this._rafId) {
      raf.cancel(this._rafId)
      this._rafId = 0
    }
  }

  public componentWillReceiveProps(props: ILayoutProps) {
    this.initProps(props)
  }

  public getBoundingLeftTop = () => {
    const leftTop = { x: 0, y: 0 }
    if (this._root) {
      const r = this._root.getBoundingClientRect()
      leftTop.x = r.left
      leftTop.y = r.top
    }
    return leftTop
  }

  public onUpdate() {
    if (this.props.onUpdate) {
      this.props.onUpdate()
    } else {
      this.setState({ update: this.state.update + 1 })
    }
  }

  public selectCallback(instance: Select) {
    this._select = instance
  }

  public edit() {
    return this._edit
  }

  public onResize(width: number, height: number) {
    if (this.state.devicePixelRatio === window.devicePixelRatio) {
      // Not zooming
      const w = Math.floor(
        width /* *this.state.devicePixelRatio / window.devicePixelRatio */
      )
      const h = Math.floor(
        height /* * this.state.devicePixelRatio / window.devicePixelRatio */
      )

      this.props.g.containersize({
        width: width,
        height: height
      })

      if (this._root) {
        const r = this._root.getBoundingClientRect()
        this.props.g.leftTop({
          x: r.left,
          y: r.top
        })
      }

      RLGDebug(DebugLevels.info)('\nonResize', this.props.name, w, h)

      if (this.state.width !== w || this.state.height !== h) {
        this.setState({ width: w, height: h })
      }
    }
  }

  public get root() {
    return this._root
  }

  private frameStart = () => {
    this._startRendering = performance.now()
    return null
  }

  private frameEnd = () => {
    const difference = performance.now() - this._startRendering
    RLGDebug(DebugLevels.timing)(`frameTime: ${difference.toFixed(4)}ms`)

    return null
  }

  public render(): React.ReactNode {
    this.initLayout()

    let style: React.CSSProperties = mainStyle(
      this.props,
      this.state,
      gLayoutRoot === this
    )

    if (this._edit) {
      this.frameStart()
      this.onResize.bind(this)
      return (
        /* style height of 100% necessary for ReactResizeDetector to work  */
        <div
          id={`${this.props.name}`}
          ref={this.onRootRef}
          style={style}
          onMouseDown={this.onParentMouseDown}
          onContextMenu={this.onParentContextMenu()}
        >
          <>
            {content(this.props, this.state, this)}

            {this.state.contextMenuActive ? (
              <ContextMenu
                commands={this.generateContextMenu(this.state.contextMenu)}
                location={this._menuLocation}
                bounds={{ width: this.state.width, height: this.state.height }}
                hideMenu={this.hideMenu}
                zIndex={999}
              />
            ) : null}

            {this.frameEnd()}
          </>
        </div>
      )
    }
    return (
      <div id={`${this.props.name}`} ref={this.onRootRef} style={style}>
        {content(this.props, this.state, this)}
      </div>
    )
  }

  private onRootRef = (elt: HTMLDivElement) => {
    if (elt) {
      this._root = elt
    }
  }

  private initProps(props: ILayoutProps) {
    this._g = this.props.g

    var w = Math.max(
      document.documentElement.clientWidth,
      window.innerWidth || 0
    )
    var h = Math.max(
      document.documentElement.clientHeight,
      window.innerHeight || 0
    )
    this._g.viewport({ width: w, height: h })

    if (this.props.containersize) {
      this._g.containersize(this.props.containersize)
    }

    if (this.props.params) {
      const params = this._g.params()
      this.props.params.forEach((value: [string, ParamValue]) => {
        params.set(value[0], value[1])
      })
    }

    if (props.animate && props.animate.active) {
      this._g.animate(1)
      this._fpsIntervalFrame =
        1000 / (props.animate.fps ? props.animate.fps : 60)
      this._lastAnimationFrame = performance.now()
      this._rafId = raf(this.animationLoop)
    }

    if (
      (props.animate && !props.animate.active) ||
      props.animate === undefined
    ) {
      this._g.animate(0)
      if (this._rafId) {
        raf.cancel(this._rafId)
        this._rafId = 0
      }
    }

    this._edit = props.service === ServiceOptions.edit
  }

  private animationLoop = (time: number) => {
    if (this.props.animate) {
      const { active, logFrameRate } = this.props.animate

      if (active) {
        // request another frame
        this._rafId = raf(this.animationLoop)

        // compute elapsed time
        const elapsed = time - this._lastAnimationFrame

        const hasTimeElapsed = elapsed > this._fpsIntervalFrame

        if (hasTimeElapsed) {
          // Do update
          let cont = true
          if (this._root) {
            const r = this._root.getBoundingClientRect()
            const w = r.right - r.left
            const h = r.bottom - r.top
            if (this.state.width != w || this.state.height != h) {
              this._g.deltaTime(0)
              this._g.animate(this.props.animate.active ? 1 : 0)
              this.setState({ width: w, height: h })
              cont = false
            }
          }

          if (cont) {
            this._g.deltaTime(time - this._lastAnimationFrame)
            this._g.animate(this.props.animate.active ? 1 : 0)

            this.setState({ update: this.state.update + 1 })

            if (logFrameRate) {
              RLGDebug(DebugLevels.timing)(
                `animation loop delta ${(
                  time - this._lastAnimationFrame
                ).toFixed(4)}`
              )
            }

            this._lastAnimationFrame = time
          } else {
            const blocks = this._g.blocks()
            if (blocks) {
              blocks.map.forEach(block => {
                block.touch()
                block.rect
              })
            }
          }
        }
      } else {
        if (this._rafId) {
          raf.cancel(this._rafId)
          this._rafId = 0
        }
      }
    }
  }

  private onWindowResize = () => {
    var w = Math.max(
      document.documentElement.clientWidth,
      window.innerWidth || 0
    )
    var h = Math.max(
      document.documentElement.clientHeight,
      window.innerHeight || 0
    )
    this._g.viewport({ width: w, height: h })

    if (this.state.devicePixelRatio !== window.devicePixelRatio) {
      const browserZoomLevel = window.devicePixelRatio * 100
      RLGDebug(DebugLevels.info)(
        `window resize zoom ${browserZoomLevel.toFixed(2)}% `
      )
    }
  }

  private initLayout = () => {
    const p = this._g.params()

    const e = p.get('editOptions') as number
    if (e) {
      this._edit = e ? true : false
    }

    this._g.reset()

    function dump(layout: Layout) {
      const params = layout._g.params()
      const containersize = layout._g.containersize()
      if (layout._count === 0 && containersize.width && containersize.height) {
        const blocks = layout._g.blocks()

        RLGDebug(DebugLevels.data)(
          `Layout debug for ${
            layout.props.name
          } with generator ${layout._g.name()}`
        )
        RLGDebug(DebugLevels.data)('params')
        params.data.forEach(item => {
          RLGDebug(DebugLevels.data)(`  ${item[0]} ${JSON.stringify(item[1])}`)
        })
        RLGDebug(DebugLevels.data)('blocks (computed position rects)')
        blocks.map.forEach((value, key) => {
          const r = value.rect
          RLGDebug(DebugLevels.data)(
            `name: ${key} x: ${r.x} y: ${r.y} width: ${r.width} height: ${
              r.height
            }`
          )
        })

        layout._count += 1
      }
    }
    RLGDebug(DebugLevels.data)(dump(this))
  }

  private onParentMouseDown = (event: React.MouseEvent) => {
    RLGDebug(DebugLevels.mouseEvents)(
      `Layout onParentMouseDown ${this.props.name} ${event.target}`
    )

    if (event.button !== 2 && this._select && this._select.selected.length) {
      this._select.clear()
    }

    if (event.button === 2) {
      event.stopPropagation()
      this.handleContextMenu(event)
    }
  }

  private onParentContextMenu = (block?: Block) => {
    return (event: React.MouseEvent) => {
      RLGDebug(DebugLevels.mouseEvents)(
        `Layout onParentContextMenu ${this.props.name} ${event.target}`
      )

      event.preventDefault()
      this.setState({ contextMenu: block, contextMenuActive: true })
    }
  }

  private generateContextMenu = (block?: Block) => {
    const menuItems: IMenuItem[] | undefined =
      this._select && this._select.commands

    if (menuItems && block && block.editor && block.editor.contextMenu) {
      const contextMenu = block.editor.contextMenu
      menuItems.push({ name: '' })
      contextMenu.forEach((item: IMenuItem) => {
        menuItems.push(item)
      })
    }

    return menuItems
  }

  private onHtmlMouseUp = (event: MouseEvent) => {
    if (event) {
      RLGDebug(DebugLevels.mouseEvents)(
        `Layout onHtmlMouseUp ${this.props.name} ${event.target}`
      )
      event.preventDefault()
      this.removeEventListeners()
      this.hideMenu()
    }
  }

  private hideMenu = () => {
    if (this.state.contextMenuActive) {
      this.setState({ contextMenu: undefined, contextMenuActive: false })
    }
  }

  private addEventListeners = () => {
    document.addEventListener('mouseup', this.onHtmlMouseUp)
  }

  private removeEventListeners = () => {
    document.removeEventListener('mouseup', this.onHtmlMouseUp)
  }

  private handleContextMenu(event: React.MouseEvent<Element>) {
    if (event.button === 2) {
      // Right click
      event.preventDefault()
      const currentTargetRect = event.currentTarget.getBoundingClientRect()
      const offsetX = event.pageX - currentTargetRect.left
      const offsetY = event.pageY - currentTargetRect.top
      this._menuLocation = { x: offsetX, y: offsetY }
      this.addEventListeners()
    } else {
      this.hideMenu()
    }
  }
}
